import { Cascader } from 'antd';
import React, { Key, Ref, useCallback, useEffect, useMemo, useState } from 'react';
import {
  Input,
  CustomFilter,
  EllipsisSpan,
  Form,
  Paging,
  Table,
  usePaging,
  Modal,
  MultiSelect,
} from '@maxtropy/components';
import type { TableRowSelection } from 'antd/es/table/interface';
import {
  DeviceTypeTree,
  formatOptionData,
  getChildNodesByParentIds,
} from '@/shared/components/CascadingMultipleSelector/utils';
import { getDeviceTypeTree, getSunLightDeviceTypeTree, getWorkSpaceLightDeviceTypeTree } from '@/api/deviceType';
import { DefaultOptionType } from 'antd/es/cascader';
import { useQuery } from '@/utils/utils';
import { apiV2ComprehensiveLabelListPost, apiV2DevicePagePost } from '@maxtropy/device-customer-apis-v2';
import { isNil } from 'lodash-es';
import { useRequest } from 'ahooks';
interface DeviceChooseProps {
  value?: any[];
  deviceType?: string;
  visible?: boolean;
  onCancel?: () => void;
  onConfirm?: (selectedDevices: any[]) => void;
}

export const columns = [
  {
    title: '设备编号',
    dataIndex: 'code',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备名称',
    dataIndex: 'name',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备S/N码',
    dataIndex: 'sn',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '所属类目',
    dataIndex: 'typeName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
  {
    title: '设备标签',
    dataIndex: 'tagsText',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v ?? '-'} />,
  },
  {
    title: '所属组织',
    dataIndex: 'customerName',
    ellipsis: { showTitle: true },
    render: (v: string) => <EllipsisSpan value={v} />,
  },
];

type SearchParams = Omit<any, 'size' | 'page' | 'deviceType'>;

const DeviceConfig = React.forwardRef((props: DeviceChooseProps, ref: Ref<{ checkedValues: any[] }>) => {
  const { value, visible, deviceType, onCancel, onConfirm } = props;
  const [checkValues, setCheckValues] = useState<any[]>([]);
  const [form] = Form.useForm();
  const pagingInfo = usePaging();
  const { pageOffset, pageSize, setTotalCount, setPageOffset } = pagingInfo;
  const [searchParams, setSearchParams] = useState<SearchParams>();
  const [otherDeviceTypeData, setOtherDeviceTypeData] = useState<DeviceTypeTree>();
  const [currentAddedCount, setCurrentAddedCount] = useState<number>();
  const [isShowSelected, setIsShowSelected] = useState<boolean>(false);
  const { data: labelData } = useRequest(() => apiV2ComprehensiveLabelListPost({ id: 3 }));
  const labelOptions = useMemo(() => {
    return (labelData?.list ?? []).map(item => ({ label: item.name, value: item.id }));
  }, [labelData]);

  const columns = useMemo(
    () => [
      {
        title: '设备编号',
        dataIndex: 'code',
        ellipsis: { showTitle: true },
        render: (v: string) => <EllipsisSpan value={v} />,
      },
      {
        title: '设备名称',
        dataIndex: 'name',
        ellipsis: { showTitle: true },
        render: (v: string) => <EllipsisSpan value={v} />,
      },
      {
        title: '设备 S/N 码',
        dataIndex: 'sn',
        ellipsis: { showTitle: true },
        render: (v: string) => <EllipsisSpan value={v} />,
      },
      {
        title: '所属类目',
        dataIndex: 'typeName',
        ellipsis: { showTitle: true },
        render: (v: string) => <EllipsisSpan value={v} />,
      },
      {
        title: '设备标签',
        dataIndex: 'tagsText',
        ellipsis: { showTitle: true },
        render: (v: string) => <EllipsisSpan value={v ?? '-'} />,
      },
      {
        title: '所属组织',
        dataIndex: 'customerName',
        ellipsis: { showTitle: true },
        render: (v: string) => <EllipsisSpan value={v} />,
      },
    ],
    []
  );

  useEffect(() => {
    if (deviceType === 'ggd') {
      getWorkSpaceLightDeviceTypeTree().then(res => setOtherDeviceTypeData(res));
    } else if (deviceType === 'sunlight') {
      getSunLightDeviceTypeTree().then(res => setOtherDeviceTypeData(res));
    } else {
      getDeviceTypeTree().then(res => setOtherDeviceTypeData(res));
    }
  }, [deviceType]);

  useEffect(() => {
    if (isNil(value)) return;

    setCheckValues(
      value.map(item => ({
        id: item.deviceId,
        name: item.name,
        code: item.code,
      }))
    );
  }, [value]);

  // 计算本次新增勾选数量
  useEffect(() => {
    const originalIds = new Set((value ?? []).map(k => k.deviceId));
    const newCount = (checkValues ?? []).filter(item => !originalIds.has(item.id)).length;
    setCurrentAddedCount(newCount);
  }, [checkValues, value]);

  const { data, isLoading } = useQuery(
    useCallback(
      async () => {
        const typeId = getChildNodesByParentIds(searchParams?.typeId as number[][], otherDeviceTypeData?.tree);
        const res = await apiV2DevicePagePost({
          ...searchParams,
          typeId: !deviceType
            ? typeId
            : getChildNodesByParentIds(
                otherDeviceTypeData?.tree.map(i => [i.id]),
                otherDeviceTypeData?.tree
              ),
          page: pageOffset,
          size: pageSize,
        });

        if (res) setTotalCount(res?.total ?? 0);
        return res.list ?? [];
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [pageOffset, pageSize, searchParams, deviceType, otherDeviceTypeData, isShowSelected]
    )
  );

  // 点击保存
  const onOk = () => {
    onConfirm?.(
      checkValues.map(item => ({
        deviceId: item.id,
        name: item.name,
        code: item.code,
      }))
    );
    handleCancel();
  };

  const rowSelection = useMemo<TableRowSelection<any>>(() => {
    return {
      type: 'checkbox',
      onChange: (selectedRowKeys: Key[]) => {
        // 直接使用 selectedRowKeys，Ant Design 会自动维护完整的选中列表（包括跨页）
        setCheckValues(
          selectedRowKeys.map(key => {
            // 从 checkValues 或 data 中查找对应的完整数据
            const found = checkValues.find(v => v.id === key) || data?.find(v => v.id === key);
            return {
              id: key,
              name: found?.name || '',
              code: found?.code || '',
            };
          })
        );
      },
      selectedRowKeys: checkValues.filter(Boolean).map(item => item.id),
      preserveSelectedRowKeys: true,
    };
  }, [checkValues, data]);

  const onFinish = (value: SearchParams) => {
    setSearchParams(value);
    setPageOffset(1);
  };
  const handleCancel = () => {
    onCancel?.();
    onReset();
  };
  const onReset = () => {
    setSearchParams({});
    form.resetFields();
    setPageOffset(1);
  };

  const filter = (inputValue: string, path: DefaultOptionType[]) =>
    path.some(option => (option.label as string).toLowerCase().indexOf(inputValue.toLowerCase()) > -1);

  const filters = (
    <CustomFilter form={form} onFinish={onFinish} colSpan={8} onReset={onReset}>
      <Form.Item name="codeOrName" label="编号/名称">
        <Input placeholder="请输入编号或名称查询" />
      </Form.Item>
      <Form.Item name="typeId" label="所属类目">
        <Cascader
          options={formatOptionData(otherDeviceTypeData)}
          allowClear={false}
          fieldNames={{ children: 'child' }}
          multiple
          maxTagCount="responsive"
          showSearch={{ filter }}
          placeholder={'请选择所属类目'}
        />
      </Form.Item>
      <Form.Item name="labelIds" label="设备标签">
        <MultiSelect style={{ width: '100%' }} placeholder="请选择" options={labelOptions} showSearch allowClear />
      </Form.Item>
    </CustomFilter>
  );

  return (
    <>
      <Modal open={visible} title="监测设备" onCancel={handleCancel} onOk={onOk} width={900} destroyOnClose>
        {filters}
        <Table
          loading={isLoading}
          rowSelection={rowSelection}
          sticky
          scroll={{ y: 300 }}
          rowKey="id"
          columns={columns}
          dataSource={data}
          bottomLeftExtra={
            <div>
              <span>本次新增勾选</span>
              <span style={{ color: '#E64242' }}>{currentAddedCount}</span>
              <span>项，</span>
              <span>总计</span>
              <span style={{ color: '#E64242' }}>{checkValues.length}</span>
              <span>项</span>
            </div>
          }
        >
          <Paging pagingInfo={pagingInfo} />
        </Table>
      </Modal>
    </>
  );
});

export default DeviceConfig;
